import { getInitials } from '../../lib/initials';
import { Link } from 'react-router-dom';
import type { UserProfile } from '../../services/users';

interface UserCardProps {
  user: UserProfile;
}

export function UserCard({ user }: UserCardProps) {
  return (
    <div className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4">
        {/* Avatar */}
        <Link to={`/profile/${user.id}`} className="flex-shrink-0">
          {user.profileImageUrl ? (
            <img
              src={user.profileImageUrl}
              alt={user.name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 font-bold text-lg">
                {getInitials(user.name)}
              </span>
            </div>
          )}
        </Link>

        {/* Name + bio — col 1 */}
        <Link to={`/profile/${user.id}`} className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 truncate">{user.name}</p>
          {user.bio && (
            <p className="text-xs text-gray-500 truncate">{user.bio}</p>
          )}
        </Link>

        {/* Phone — col 2 */}
        <div className="w-40 hidden sm:flex items-center gap-1 text-sm text-gray-600">
          {user.phone ? (
            <>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <a href={`tel:${user.phone}`} className="hover:text-primary-600 truncate" onClick={(e) => e.stopPropagation()}>
                {user.phone}
              </a>
            </>
          ) : (
            <span className="text-gray-300 text-xs">–</span>
          )}
        </div>

        {/* Email — col 3 */}
        <div className="w-48 hidden md:flex items-center gap-1 text-sm text-gray-600">
          {user.contactEmail ? (
            <>
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <a href={`mailto:${user.contactEmail}`} className="hover:text-primary-600 truncate" onClick={(e) => e.stopPropagation()}>
                {user.contactEmail}
              </a>
            </>
          ) : (
            <span className="text-gray-300 text-xs">–</span>
          )}
        </div>
      </div>

      {/* Mobile: show contact below on small screens */}
      {(user.phone || user.contactEmail) && (
        <div className="mt-2 pl-16 flex flex-wrap gap-3 sm:hidden text-sm text-gray-600">
          {user.phone && (
            <a href={`tel:${user.phone}`} className="flex items-center gap-1 hover:text-primary-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              {user.phone}
            </a>
          )}
          {user.contactEmail && (
            <a href={`mailto:${user.contactEmail}`} className="flex items-center gap-1 hover:text-primary-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {user.contactEmail}
            </a>
          )}
        </div>
      )}
    </div>
  );
}
